
import type { FacetsDTO } from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { AsidePanelFacetsMessages, FacetItem, RegularFilter } from '../../models'

import type { FacetsInfoCrudService } from '../../services'
import type { EpbAccordionSection } from '../shared/epb-accordion.component.vue'

import type { EpbSelectOption } from '../shared/epb-select'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, onBeforeMount, onUpdated, reactive, ref, watch } from 'vue'
import { useInstance } from '../../composables'
import { CONTAINER } from '../../config/inversify.config'

import { TYPES } from '../../config/inversify.types'
import { ColorEnum, FacetSortTypeEnum, FacetStatsUnitEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import EpbAccordion from '../shared/epb-accordion.component.vue'
import { EpbAsideForm } from '../shared/epb-aside-form'
import { EpbBadge } from '../shared/epb-badge'
import { EpbButton } from '../shared/epb-button'
import { EpbSelect } from '../shared/epb-select'

import { EpbText } from '../shared/epb-text'

import { EpbTextbox } from '../shared/epb-textbox'
import TransitionSize from '../transition-size.component.vue'
import EpbFacetsSettingsSection from './epb-facets-settings-section.component.vue'

export default defineComponent({
  name: 'EpbFacetsAside',
  components: {
    EpbText,
    TransitionSize,
    EpbBadge,
    EpbFacetsSettingsSection,
    EpbButton,
    EpbAccordion,
    EpbAsideForm,
    EpbSelect,
    EpbTextbox,
  },
  props: {
    facetSelected: {
      type: Object as PropType<FacetItem>,
      required: true,
    },
    item: {
      type: Object as PropType<FacetsDTO>,
      required: true,
    },
    pivots: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    metadata: {
      type: Array as PropType<FacetItem[]>,
      default: () => [],
    },
    hasPendingMetadata: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object as PropType<AsidePanelFacetsMessages>,
      required: true,
    },
    inheritance: Boolean,
    opened: Boolean,
  },
  setup(props, ctx: SetupContext) {
    const baseClass = 'epb-facets-aside'

    const { emit } = ctx

    const { instanceConfig } = useInstance()

    const service = CONTAINER.get<FacetsInfoCrudService>(TYPES.FacetsInfoCrudServide)

    const facetItem = reactive({
      ...props.facetSelected,
      unit: props.facetSelected.unit ?? FacetStatsUnitEnum.CURRENCY,
      decimalDigits: props.facetSelected.decimalDigits,
    })

    const itemValue = ref<FacetItem>({ title: '', subtitle: '' })

    const isActiveAddValue = ref(false)

    const facetsSettingsItems = ref<FacetItem[]>([])

    const listFacetsValuesItems = ref()
    const editValueItem = ref()

    const sectionValues: EpbAccordionSection[] = [
      { id: props.messages.metadata.title, title: props.messages.metadata.title },
    ]

    const sortTypeOptions = computed((): EpbSelectOption[] => {
      const options = Object.values(FacetSortTypeEnum)
      return options.map(option => ({
        value: option,
        label: capitalizeSentence(option.toLowerCase().split('_').join(' ')),
      }))
    })

    const unitOptions = computed((): EpbSelectOption[] => {
      return Object.values(FacetStatsUnitEnum).map(option => ({
        value: option,
        label: capitalizeSentence(option),
      }))
    })

    const isSliderFacet = computed(() => {
      return !!props.item.config.facetableFields.sliderFacets?.some(
        facet => facet.field === facetItem.title,
      )
    })

    const decimalDigitsInput = computed({
      get: () =>
        facetItem.decimalDigits === undefined || facetItem.decimalDigits === null
          ? ''
          : String(facetItem.decimalDigits),
      set: (value: string) => {
        if (value === '' || value === null || value === undefined) {
          facetItem.decimalDigits = undefined
          return
        }
        const parsed = Number(value)
        facetItem.decimalDigits = Number.isNaN(parsed) ? undefined : parsed
      },
    })

    const isVisibleValuesSection = computed(() => {
      return instanceConfig.value.allowMetadataSectionInFacetsAside
    })

    const isItemSelected = computed(() => {
      return facetsSettingsItems.value.find(facetsSettingsItem => facetsSettingsItem.selected)
    })

    const existFacetMetadata = computed((): boolean => {
      return !!facetsSettingsItems.value.find(
        facetSettingItem =>
          facetSettingItem.title === itemValue.value.title &&
          facetSettingItem.subtitle === itemValue.value.subtitle,
      )
    })

    const isItemDuplicated = computed(() => {
      if (!existFacetMetadata.value) {
        return false
      }

      if (!isItemSelected.value) {
        return true
      }

      return (
        itemValue.value.title !== editValueItem.value.title ||
        itemValue.value.subtitle !== editValueItem.value.subtitle
      )
    })

    const clear = (): void => {
      facetItem.prettyName = ''
      facetItem.subtitle = FacetSortTypeEnum.ALPHABETICALLY
      facetItem.unit = FacetStatsUnitEnum.CURRENCY
      facetItem.decimalDigits = undefined
      itemValue.value = { title: '', subtitle: FacetSortTypeEnum.ALPHABETICALLY }
      facetsSettingsItems.value = []
    }

    const handleSubmit = (): void => {
      const newFacetItem = {
        facet: facetItem.title,
        metadata: facetsSettingsItems.value,
      }

      if (!listFacetsValuesItems.value?.results.length) {
        listFacetsValuesItems.value = {
          numFound: 1,
          results: [newFacetItem],
        }
      } else {
        const existFacetItem = listFacetsValuesItems.value.results.findIndex(
          value => value.facet === facetItem.title,
        )
        if (existFacetItem === -1) {
          listFacetsValuesItems.value.results.push(newFacetItem)
        } else {
          listFacetsValuesItems.value.results.map(facetValue => {
            return facetsSettingsItems.value.map(metadata => {
              const existFacetValue = facetValue.metadata.findIndex(
                facet => facet && facet.key === metadata.title && facet.value === metadata.subtitle,
              )

              return {
                ...facetValue,
                metadata:
                  existFacetValue === -1
                    ? facetValue.metadata.push(facetValue.metadata[existFacetValue])
                    : facetValue.metadata,
              }
            })
          })
        }
      }

      emit('updateMetadata', facetsSettingsItems.value, listFacetsValuesItems.value)
      emit('submit', {
        ...facetItem,
        ...(isSliderFacet.value
          ? {
              unit: facetItem.unit ?? FacetStatsUnitEnum.CURRENCY,
              decimalDigits: facetItem.decimalDigits,
            }
          : {}),
      })
    }

    const removeItemValue = (): void => {
      itemValue.value.title = ''
      itemValue.value.subtitle = ''
    }

    const addSettingItem = (): void => {
      facetsSettingsItems.value.push({ ...itemValue.value })
    }

    const editSettingItem = (): void => {
      facetsSettingsItems.value.forEach(facetSettingItem => {
        if (facetSettingItem.title === editValueItem.value.title) {
          facetSettingItem.title = itemValue.value.title
          facetSettingItem.subtitle = itemValue.value.subtitle
          facetSettingItem.selected = !facetSettingItem.selected
        }
      })
    }

    const clickSettingItem = (): void => {
      if (isItemSelected.value) {
        editSettingItem()
      } else {
        addSettingItem()
      }

      removeItemValue()
    }

    const editItem = (itemToEdit: FacetItem): void => {
      editValueItem.value = itemToEdit
      isActiveAddValue.value = true
      facetsSettingsItems.value.forEach(facetSettingItem => {
        if (
          facetSettingItem.title === itemToEdit.title &&
          facetSettingItem.subtitle === itemToEdit.subtitle
        ) {
          facetSettingItem.selected = !facetSettingItem.selected
          itemToEdit.selected = !itemToEdit.selected
          if (itemToEdit.selected) {
            itemValue.value.title = itemToEdit.title
            itemValue.value.subtitle = itemToEdit.subtitle
          } else {
            removeItemValue()
          }
        }
      })
    }

    const removeItem = (itemToRemove: FacetItem): void => {
      facetsSettingsItems.value = facetsSettingsItems.value.filter(
        facetSettingItem =>
          facetSettingItem.title !== itemToRemove.title ||
          facetSettingItem.subtitle !== itemToRemove.subtitle,
      )
    }

    const listFacetsValues = async (): Promise<void> => {
      try {
        const isSearchContext = props.item.endpointId === DomainEnum.SEARCH

        const params = {
          pivots: Object.values(props.pivots).map(pivot => ({
            name: pivot.config.id,
            values: pivot.selectedValues,
          })),
          endpoint: props.item.endpointId,
          facet: facetItem.title,
          ...(isSearchContext
            ? {
                queries:
                  props.item.criteria.context?.[0]?.values[0] === '*'
                    ? []
                    : props.item.criteria.context?.[0]?.values,
              }
            : {
                browseField: props.item.criteria.context?.[0]?.values[0],
                browseValue: props.item.criteria.context?.[1]?.values[0],
              }),
          inheritance: props.inheritance,
        }

        listFacetsValuesItems.value = await service.list(params)

        facetsSettingsItems.value = []

        listFacetsValuesItems.value?.results.forEach(dataItem => {
          if (dataItem.metadata) {
            dataItem.metadata.forEach(metadataItem => {
              facetsSettingsItems.value.push({
                title: metadataItem.key,
                subtitle: metadataItem.value,
              })
            })
          }
        })

        if (props.hasPendingMetadata) {
          facetsSettingsItems.value = [...props.metadata]
        }
      } catch (error) {
        throw error
      }
    }

    watch(
      () => props.facetSelected,
      async () => {
        Object.assign(facetItem, {
          ...props.facetSelected,
          unit: props.facetSelected.unit ?? FacetStatsUnitEnum.CURRENCY,
          decimalDigits: props.facetSelected.decimalDigits,
        })
        isActiveAddValue.value = false
        await listFacetsValues()
      },
      { deep: true },
    )

    watch(
      () => props.opened,
      async isOpened => {
        if (isOpened) {
          Object.assign(facetItem, {
            ...props.facetSelected,
            unit: props.facetSelected.unit ?? FacetStatsUnitEnum.CURRENCY,
            decimalDigits: props.facetSelected.decimalDigits,
          })
          await listFacetsValues()
        }
      },
    )

    onUpdated(() => {
      itemValue.value = { title: '', subtitle: '' }
    })

    onBeforeMount(async () => await listFacetsValues())

    return {
      baseClass,
      facetItem,
      itemValue,
      isActiveAddValue,
      facetsSettingsItems,
      sectionValues,
      sortTypeOptions,
      unitOptions,
      isSliderFacet,
      decimalDigitsInput,
      isVisibleValuesSection,
      isItemSelected,
      existFacetMetadata,
      isItemDuplicated,
      clear,
      handleSubmit,
      clickSettingItem,
      editItem,
      removeItem,
      ColorEnum,
    }
  },
})
